import React, { useEffect, useRef, useState } from "react";
import "./Header.css";
import { HiMiniPencilSquare } from "react-icons/hi2";
import { FaBars, FaBell, FaCheck, FaQuestionCircle } from "react-icons/fa";
import FalconLogo from "../../assets/falcon.png";
import { useMsal } from "@azure/msal-react";
import Select from "react-select";
import axiosClient from "../../api-client/AxiosClient";
import Joyride from "react-joyride";

const Header = ({ isOpen, setIsOpen, run, setRun }) => {
  const { Dashboard, DataSource, Upload, STTMapping, Administration } =
    JSON.parse(sessionStorage.getItem("rolesAndPermission") || "{}");

  const { instance } = useMsal();
  const menuRef = useRef(null);
  const notificationRef = useRef(null);
  const [corporationDropdown, setCorporationDropdown] = useState();
  const [corporationList, setCorporationList] = useState([]);
  const [showOrganizationPopup, setShowOrganizationPopup] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [steps, setSteps] = useState([
    {
      target: ".header-icon-joyride-class",
      content:
        "Falcon Mapping delivers discipline, intelligence, and simplicity to data migration and reporting, turning a traditionally complex and error-prone process into a streamlined, dependable solution for enterprises.",
      disableBeacon: true,
    },
    {
      target: ".header-corporation-joyride-class",
      content:
        "Seamlessly switch between all organizations you have access to across the platform.",
      disableBeacon: true,
    },
    {
      target: ".header-profile-joyride-class",
      content:
        "Access your account, manage profile settings, and log out across the platform.",
      disableBeacon: true,
    },
  ]);

  useEffect(() => {
    let newSteps = [...steps];
  
    if (Dashboard?.canView) {
      newSteps.push(
        {
          target: ".sidebar-dashboard-joyride-class",
          content:
            "Track overall mapping progress, review system warnings, and monitor completion status across objects and users.",
          disableBeacon: true,
          placement: "right",
        },
      );
    }
    if (DataSource?.canView) {
      newSteps.push(
        {
          target: ".sidebar-datasource-joyride-class",
          content:
            "Connect to multiple data sources and seamlessly configure, manage, and validate source and target system connections.",
          disableBeacon: true,
          placement: "right",
        },
      );
    }
    if (Upload?.canView) {
      newSteps.push(
        {
          target: ".sidebar-upload-joyride-class",
          content:
            "Upload partially or fully prepared mapping documents for target system objects, with guidance on supported formats and validation.",
          disableBeacon: true,
          placement: "right",
        },
      );
    }
    if (STTMapping?.canView) {
      newSteps.push(
        {
          target: ".sidebar-stt-joyride-class",
          content:
            "Access existing mapping documents to complete, validate, and refine source-to-target field mappings, supported by intelligent suggestions and error highlights.",
          disableBeacon: true,
          placement: "right",
        },
      );
    }
    newSteps.push(
      {
        target: ".sidebar-ai-joyride-class",
        content:
          "Engage with Bluegecko's AI-powered assistant to quickly locate relevant mappings, ask questions, and receive contextual recommendations.",
        disableBeacon: true,
        placement: "right",
      },
    );
    if (Administration?.canView) {
      newSteps.push(
        {
          target: ".sidebar-settings-joyride-class",
          content:
            "Admin Settings provides centralized control over key system configurations. It allows you to manage users and roles, configure organizational settings, and define objects and templates to maintain consistency in mapping and reporting",
          disableBeacon: true,
          placement: "right",
        },
      );
    }

    setSteps(newSteps);
  }, []);

  // Notification states
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Safe function to get team member name (you may need to fetch user details)
  const getTeamMemberName = (assignedTo) => {
    try {
      // For now, showing ID. You might want to fetch actual user names
      return assignedTo || "Unknown Member";
    } catch (error) {
      return "Unknown Member";
    }
  };

  // Get priority badge color and text
  const getPriorityInfo = (priority) => {
    // Handle both string and numeric priority values
    const priorityValue =
      typeof priority === "string" ? priority.toLowerCase() : priority;
    const priorityNum = parseInt(priority);

    // Check for string values first
    if (typeof priority === "string") {
      switch (priorityValue) {
        case "high":
          return {
            color: "bg-red-100 text-red-800 border-red-200",
            text: "High",
          };
        case "medium":
          return {
            color: "bg-yellow-100 text-yellow-800 border-yellow-200",
            text: "Medium",
          };
        case "low":
          return {
            color: "bg-green-100 text-green-800 border-green-200",
            text: "Low",
          };
      }
    }

    // Check for numeric values
    switch (priorityNum) {
      case 1:
        return {
          color: "bg-red-100 text-red-800 border-red-200",
          text: "High",
        };
      case 2:
        return {
          color: "bg-yellow-100 text-yellow-800 border-yellow-200",
          text: "Medium",
        };
      case 3:
        return {
          color: "bg-green-100 text-green-800 border-green-200",
          text: "Low",
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800 border-gray-200",
          text: "Medium",
        };
    }
  };

  const toggleDropdown = () => setIsMenuOpen(!isMenuOpen);

  // Fetch notifications
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axiosClient.get("/api/ToDo/assigned-to");
      if (response && response.data) {
        setNotifications(Array.isArray(response.data) ? response.data : []);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  // Toggle notification popup
  const toggleNotificationPopup = () => {
    setShowNotificationPopup(!showNotificationPopup);
    if (!showNotificationPopup) {
      fetchNotifications();
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (id) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  // Handle task completion
  const handleTaskComplete = async (taskId) => {
    try {
      await axiosClient.put(`/api/ToDo/${taskId}/mark-read`);
      // Remove from notifications list
      setNotifications((prev) => prev.filter((notif) => notif.id !== taskId));
    } catch (error) {
      console.error("Error completing task:", error);
    }
  };

  const handleLogout = () => {
    instance.logout().catch((e) => {
      console.error(e);
    });
  };

  const handleCorporationSelect = async (selected) => {
    if (corporationDropdown?.value === selected?.value) {
      return;
    }
    setCorporationDropdown(selected);
    localStorage.setItem("selectedCorporation", JSON.stringify(selected));
    window.dispatchEvent(new Event("localStorageUpdated"));

    // Dispatch custom event for same-window communication
    window.dispatchEvent(
      new CustomEvent("customStorageChange", {
        detail: {
          key: "selectedCorporation",
          newValue: JSON.stringify(selected),
        },
      })
    );
  };

  const handleEditOrganization = () => {
    setShowOrganizationPopup(true);
  };

  const handlePopupCorporationChange = (selected) => {
    handleCorporationSelect(selected);
    setShowOrganizationPopup(false);
  };

  const handleOutsideClick = (e) => {
    if (e.target.classList.contains("popup-overlay")) {
      setShowOrganizationPopup(false);
    }
  };

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotificationPopup(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setLoggedInUser(
      JSON.parse(
        sessionStorage.getItem(
          JSON.parse(sessionStorage.getItem("msal.account.keys"))[0]
        )
      ).name.split(" ")
    );
  }, []);

  useEffect(() => {
    const fetchCorporations = async () => {
      const response = await axiosClient.get(
        "/api/corporation/GetUserCorporations"
      );
      if (response && response.data) {
        const data = Array.isArray(response.data) ? response.data : [];
        const formatted = data.map((corp) => ({
          label: corp.name,
          value: corp.corporationId,
        }));
        setCorporationList(formatted);

        // Check if there's already a stored corporation
        const storedCorporation = localStorage.getItem("selectedCorporation");
        if (storedCorporation) {
          setCorporationDropdown(JSON.parse(storedCorporation));
        } else if (formatted.length > 0) {
          // Set first corporation as default
          setCorporationDropdown(formatted[0]);
          localStorage.setItem(
            "selectedCorporation",
            JSON.stringify(formatted[0])
          );
          window.dispatchEvent(new Event("localStorageUpdated"));
        }
      } else {
        setCorporationList([]);
      }
    };
    fetchCorporations();
  }, []);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if (status === "finished" || status === "skipped") {
      setRun(true);
    }
  };

  return (
    <>
      <Joyride
        steps={steps}
        run={!run}
        continuous
        showProgress
        showSkipButton
        disableOverlayClose
        disableScrolling
        styles={{
          options: {
            zIndex: 10000,
          },
          overlay: {
            zIndex: 9999,
          },
          tooltipContainer: {
            zIndex: 10001,
          },
          buttonNext: {
            backgroundImage: "linear-gradient(to right, #3b82f6, #2563eb)",
            color: "#fff",
          },
          buttonBack: {
            color: "#2563eb",
            marginRight: 10,
          },
        }}
        callback={handleJoyrideCallback}
      />
      <header className="header">
        <div className="left-section">
          <button className="sidebar-toggle" onClick={() => setIsOpen(!isOpen)}>
            <FaBars />
          </button>

          <div className="flex items-center header-icon-joyride-class">
            <img src={FalconLogo} alt="NextGenlytics Logo" className="logo" />
          </div>
        </div>
        <div style={{ marginLeft: "40px", fontSize: "14px", color: "#6b7280" }}>
          <span> BlueGecko / Falcon Mapping</span>
        </div>

        <div
          className="relative w-full max-w-md mx-auto invisible"
          style={{ fontSize: "14px", color: "#6b7280" }}
        >
          <span className="absolute inset-y-0 left-0 flex items-center pl-3">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1117.5 10a7.5 7.5 0 01-4.85 6.65z"
              />
            </svg>
          </span>
          <input
            type="text"
            placeholder="Search objects, modules & mapping progress"
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm text-gray-700"
          />
        </div>
        <FaQuestionCircle
          className="text-gray-600 text-lg cursor-pointer mr-4"
          title="Help"
          onClick={() => setRun(false)}
        />
        <div className="user-info">
          {window.location.pathname !== "/admin-interface" && (
            <>
              {/* Notification Icon */}
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={toggleNotificationPopup}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200 cursor-pointer"
                  title="Notifications"
                >
                  <FaBell className="text-gray-600 text-lg" />
                  {notifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                      {notifications.length > 99 ? "99+" : notifications.length}
                    </span>
                  )}
                </button>

                {/* Notification Popup - Tabular Format */}
                {/* Notification Popup - Tabular Format */}
                {showNotificationPopup && (
                  <div className="absolute right-0 mt-2 w-[600px] bg-white rounded-lg shadow-xl border z-50">
                    {/* Header */}
                    <div className="px-4 py-3 border-b bg-gray-50">
                      <h3 className="font-semibold text-gray-800 text-lg">
                        To-Do List
                      </h3>
                      <p className="text-sm text-gray-600">
                        You have {notifications.length} pending tasks
                      </p>
                    </div>

                    {/* Table Content */}
                    <div>
                      {loading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <span className="ml-2 text-gray-600">Loading...</span>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <FaBell className="mx-auto text-3xl mb-2 opacity-50" />
                          <p>No notifications found</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          {/* Table Header */}
                          <div className="bg-gray-100 border-b">
                            <div className="grid grid-cols-12 gap-2 px-4 py-3 text-sm font-medium text-gray-700">
                              <div className="col-span-3">Team Member</div>
                              <div className="col-span-6">Task</div>
                              <div className="col-span-2">Priority</div>
                              <div className="col-span-1">Action</div>
                            </div>
                          </div>
                          {/* Table Rows with conditional scrolling */}
                          <div
                            className={`divide-y divide-gray-200 ${
                              notifications.length > 2
                                ? "max-h-48 overflow-y-auto"
                                : ""
                            }`}
                          >
                            {notifications.map((notification, index) => (
                              <div key={notification.id || index}>
                                {/* Main Row */}
                                <div
                                  className="bg-white hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
                                  onClick={() =>
                                    toggleRowExpansion(notification.id || index)
                                  }
                                >
                                  <div className="grid grid-cols-12 gap-2 px-4 py-3 items-center">
                                    {/* Team Member */}
                                    <div className="col-span-3 flex items-center space-x-2">
                                      <span className="text-sm text-gray-900 truncate">
                                        {getTeamMemberName(
                                          notification.assignedTo
                                        )}
                                      </span>
                                    </div>

                                    {/* Task */}
                                    <div className="col-span-6">
                                      <p className="text-sm font-medium text-gray-900 truncate">
                                        {notification.subject ||
                                          "Untitled Task"}
                                      </p>
                                      <p className="text-xs text-gray-500 truncate">
                                        Due:{" "}
                                        {new Date(
                                          notification.dueDate
                                        ).toLocaleDateString()}
                                      </p>
                                    </div>

                                    {/* Priority */}
                                    <div className="col-span-2 flex justify-center">
                                      <span
                                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                                          getPriorityInfo(notification.priority)
                                            .color
                                        }`}
                                      >
                                        {
                                          getPriorityInfo(notification.priority)
                                            .text
                                        }
                                      </span>
                                    </div>

                                    <div className="col-span-1 flex justify-start pl-4">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleTaskComplete(notification.id);
                                        }}
                                        className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 hover:bg-green-200 transition-colors duration-200"
                                        title="Mark as complete"
                                      >
                                        <FaCheck className="text-green-600 text-sm" />
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                {/* Expanded Description Row */}
                                {expandedRows.has(notification.id || index) &&
                                  notification.description && (
                                    <div className="bg-gray-50 border-t">
                                      <div className="px-4 py-3">
                                        <div className="ml-8">
                                          <div className="max-h-32 overflow-y-auto">
                                            <p className="text-sm text-gray-700 leading-relaxed">
                                              <strong>Description:</strong>{" "}
                                              {notification.description}
                                            </p>
                                          </div>
                                          <div className="mt-2 text-xs text-gray-500">
                                            <span>
                                              Created:{" "}
                                              {new Date(
                                                notification.createdOn
                                              ).toLocaleString()}
                                            </span>
                                            {notification.updatedOn && (
                                              <span className="ml-4">
                                                Updated:{" "}
                                                {new Date(
                                                  notification.updatedOn
                                                ).toLocaleString()}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Separator */}
              <div className="separator-line mx-3"></div>

              {corporationDropdown && (
                <div
                  className="organization-hover-container"
                  onClick={
                    corporationList?.length > 1
                      ? handleEditOrganization
                      : undefined
                  }
                  style={{
                    cursor: corporationList?.length > 1 ? "pointer" : "default",
                  }}
                >
                  <span
                    className="organization-name header-corporation-joyride-class"
                    style={{ fontSize: "14px", color: "#6b7280" }}
                  >
                    {corporationDropdown.label}
                  </span>
                  {corporationList?.length > 1 && (
                    <span className="edit-organization-btn">
                      <HiMiniPencilSquare />
                    </span>
                  )}
                </div>
              )}
              <div className="separator-line"></div>
            </>
          )}
          <div className="relative" ref={menuRef}>
            {/* Avatar Button */}
            <span
              onClick={toggleDropdown}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-yellow-500 text-white font-bold cursor-pointer header-profile-joyride-class"
            >
              {loggedInUser[0]?.charAt(0)}
              {loggedInUser.length > 1 &&
                loggedInUser[loggedInUser.length - 1]?.charAt(0)}
            </span>

            {/* Dropdown */}
            {isMenuOpen && (
              <div className="absolute right-0 mt-2 w-auto bg-white rounded-lg shadow-xl border z-50">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <span className="font-semibold text-gray-800">BlueGecko</span>
                  <button
                    onClick={handleLogout}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Sign out
                  </button>
                </div>

                {/* Profile Info */}
                <div className="flex items-center gap-[12px] px-4 py-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-yellow-500 text-white font-bold">
                    {loggedInUser[0]?.charAt(0)}
                    {loggedInUser.length > 1 &&
                      loggedInUser[loggedInUser.length - 1]?.charAt(0)}
                  </div>
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900">
                      {loggedInUser[0]}{" "}
                      {loggedInUser.length > 1 &&
                        loggedInUser[loggedInUser.length - 1]}
                    </span>
                    <span className="text-sm text-gray-600">
                      {JSON.parse(
                        sessionStorage.getItem(
                          JSON.parse(
                            sessionStorage.getItem("msal.account.keys")
                          )[0]
                        )
                      ).username || ""}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Organization Selection Popup */}
      {showOrganizationPopup && (
        <div className="popup-overlay" onClick={handleOutsideClick}>
          <div className="popup-content">
            <h3 className="popup-title">Select Organization</h3>
            <div className="popup-body">
              <Select
                value={corporationDropdown}
                onChange={handlePopupCorporationChange}
                options={corporationList}
                placeholder="Select Corporation"
                isSearchable
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    minHeight: "40px",
                    borderColor: state.isFocused ? "#3B82F6" : "#D1D5DB",
                    boxShadow: state.isFocused ? "0 0 0 1px #3B82F6" : "none",
                    "&:hover": {
                      borderColor: "#9CA3AF",
                    },
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? "#3B82F6"
                      : state.isFocused
                      ? "#EFF6FF"
                      : "white",
                    color: state.isSelected ? "white" : "#374151",
                    "&:hover": {
                      backgroundColor: state.isSelected ? "#3B82F6" : "#EFF6FF",
                    },
                  }),
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
